import { createAppRequest } from '@/services/request';
import { runInCurrentTenantScope } from './tenantScope';

const appRequest = createAppRequest();
const request: typeof appRequest = (url, options) =>
  runInCurrentTenantScope(() => appRequest(url, options));

export type TechManagerResource =
  | 'activities'
  | 'organizations'
  | 'contacts'
  | 'leads'
  | 'demands'
  | 'experts'
  | 'technologies'
  | 'suppliers'
  | 'opportunities';

export type TechManagerEntityType =
  | 'activity'
  | 'organization'
  | 'contact'
  | 'lead'
  | 'demand'
  | 'expert'
  | 'technology'
  | 'supplier'
  | 'opportunity'
  | 'conversion_business';

export type TechManagerInput = Record<string, unknown>;

export interface TechManagerListQuery {
  page?: number;
  pageSize?: 10 | 20 | 50;
  keyword?: string;
  status?: string;
  ownerMemberUuid?: string;
  industry?: string;
  region?: string;
  startAtFrom?: string;
  startAtTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
}

export interface TechManagerRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  ownerMemberUuid?: string | null;
  ownerName?: string | null;
  status?: string;
  stage?: string;
  industry?: string | null;
  region?: string | null;
  isTechnologySupplier?: boolean;
  [key: string]: unknown;
}

export interface LeadRecord extends TechManagerRecord {
  type?: string | null;
  sourceType?: string | null;
  sourceActivityId?: string | null;
  sourceName?: string | null;
  organizationId?: string | null;
  contactId?: string | null;
  priority: string;
  nextAction?: string | null;
  description?: string | null;
  convertedAt?: string | null;
  followUps?: TechManagerFollowUp[];
  activityLogs?: TechManagerRecord[];
}

export interface DemandRecord extends TechManagerRecord {
  code: string;
  organizationId?: string | null;
  contactId?: string | null;
  sourceLeadId?: string | null;
  priority: string;
  category?: string | null;
  expertise?: string | null;
  description?: string | null;
  budgetMin?: string | null;
  budgetMax?: string | null;
  expectedDurationDays?: number | null;
  nextAction?: string | null;
  followUps?: TechManagerFollowUp[];
  activityLogs?: TechManagerRecord[];
}

export type DemandReportType =
  | 'TECHNICAL_DEMAND_CONFIRMATION'
  | 'TECHNICAL_DIAGNOSIS_FEASIBILITY'
  | 'TECHNICAL_RESEARCH_INTERVIEW_OUTLINE'
  | 'TECHNICAL_EXPERT_CAPABILITY_REQUIREMENTS'
  | 'TECHNICAL_SUPPLY_DEMAND_PROFILE';

export interface DemandEvidenceReference {
  id: string;
  demandId: string;
  runId: string;
  sourceType: string;
  sourceId: string;
  sourceSnapshot: Record<string, unknown>;
  locator?: string | null;
}

export interface DemandAnalysisRun {
  id: string;
  roundNo: number;
  modelRef?: string | null;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  sceneCode: string;
  inputSnapshot: Record<string, unknown> & {
    additionalFacts?: string;
    evidence?: Array<Record<string, unknown>>;
  };
  startedAt: string;
  completedAt?: string | null;
  errorCategory?: string | null;
}

export interface DemandReportReview {
  confirmationNo: number;
  contentSnapshot: Record<string, unknown>;
  reason?: string | null;
  createdAt: string;
}

export interface DemandReportVersion {
  id: string;
  reportType: DemandReportType;
  runId: string;
  versionNo: number;
  content: Record<string, unknown>;
  createdAt: string;
  review?: DemandReportReview;
}

export interface StartDemandAnalysisInput {
  idempotencyKey: string;
  additionalFacts?: string;
  evidence: Array<{
    sourceType: string;
    sourceId: string;
    locator?: string;
  }>;
}

export interface ConfirmDemandReportInput {
  reportVersionId: string;
  expectedConfirmationNo: number;
  contentSnapshot: Record<string, unknown>;
  reason?: string;
}

export interface DemandAnalysisHistory {
  runs: DemandAnalysisRun[];
  reportVersions: DemandReportVersion[];
  confirmations: Array<
    DemandReportReview & {
      reportVersionId: string;
      reportType: DemandReportType;
    }
  >;
}

export interface DemandAnalysisResult {
  run: DemandAnalysisRun;
  evidence: DemandEvidenceReference[];
  reportVersions: DemandReportVersion[];
}

export interface TechManagerFollowUp extends TechManagerRecord {
  entityType: string;
  entityId: string;
  type: string;
  content: string;
  ownerName?: string | null;
  occurredAt: string;
  nextAction?: string | null;
}

export interface ConvertTechManagerLeadInput {
  idempotencyKey: string;
  name?: string;
  organizationId?: string | null;
  contactId?: string | null;
  priority?: string;
  category?: string | null;
  industry?: string | null;
  expertise?: string | null;
  description?: string | null;
  budgetMin?: string | null;
  budgetMax?: string | null;
  expectedDurationDays?: number | null;
  nextAction?: string | null;
}

export interface ConvertTechManagerLeadResult {
  leadId: string;
  demandId: string | null;
  organizationId?: string | null;
  contactId?: string | null;
  replayed?: boolean;
  demand?: DemandRecord;
}

export interface PageResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface TechManagerDeletePreviewItem {
  resource: string;
  id: string;
  name: string;
  reason?: string;
}

export interface TechManagerDeletePreview {
  allowed: boolean;
  target: TechManagerDeletePreviewItem;
  cascade: TechManagerDeletePreviewItem[];
  blocked: TechManagerDeletePreviewItem[];
}

export interface DashboardQuery {
  from?: string;
  to?: string;
  ownerMemberUuid?: string;
}

export type StatisticsTopic = 'overview' | 'scale' | 'conversion' | 'process' | 'results' | 'efficiency';
export interface StatisticsItem { label: string; value: number; }
export interface StatisticsResponse {
  metrics: StatisticsItem[];
  trends?: Array<{ title: string; items: Array<{ label: string; values: StatisticsItem[] }> }>;
  distributions?: Array<{ title: string; items: StatisticsItem[] }>;
  ownerItems?: Array<{ memberUuid: string; leads: number; demands: number; opportunities: number; engagements: number; businesses: number; value: number }>;
}

/** 工作台聚合 DTO；总数必须由服务端按当前租户和明确口径计算。 */
export interface WorkbenchResponse {
  pendingLeads?: TechManagerRecord[];
  pendingLeadsCount?: number;
  activeOpportunities?: TechManagerRecord[];
  activeOpportunitiesCount?: number;
  recentActivities?: TechManagerRecord[];
  keyOpportunities?: TechManagerRecord[];
  recentLogs?: WorkbenchLog[];
}

export interface WorkbenchLog {
  id: string;
  title: string;
  detail?: string | null;
  createdAt: string;
  operatorMemberUuid?: string | null;
  operatorName?: string | null;
}

export interface CreateTechManagerOpportunityInput {
  name: string;
  type: string;
  priority: string;
  ownerMemberUuid?: string;
  ownerName?: string;
  solutionSummary?: string | null;
  background?: string | null;
  idempotencyKey: string;
}

export interface ConfirmTechManagerOpportunityInput {
  evidenceStatement: string;
  confirmationDate: string;
  expectedVersion: number;
  idempotencyKey: string;
  attachmentIds?: string[];
}

export async function getTechManagerList<
  T extends TechManagerRecord = TechManagerRecord,
>(
  resource: TechManagerResource,
  query: TechManagerListQuery = {},
): Promise<PageResult<T>> {
  const result = await request<
    | PageResult<T>
    | T[]
    | {
        items?: T[];
        list?: T[];
        data?: T[];
        total?: number;
        page?: number;
        pageSize?: number;
      }
  >(`/bff/tech-manager/${resource}`, {
    method: 'GET',
    params: query,
  });
  if (Array.isArray(result)) {
    return {
      items: result,
      total: result.length,
      page: query.page || 1,
      pageSize: query.pageSize || 10,
    };
  }
  const candidate = result as
    | {
        items?: T[];
        list?: T[];
        data?: T[];
        total?: number;
        page?: number;
        pageSize?: number;
      }
    | undefined;
  const items = Array.isArray(candidate?.items)
    ? candidate.items
    : Array.isArray(candidate?.data)
      ? candidate.data
      : Array.isArray(candidate?.list)
        ? candidate.list
        : [];
  return {
    items,
    total:
      typeof candidate?.total === 'number' ? candidate.total : items.length,
    page: candidate?.page ?? query.page ?? 1,
    pageSize: candidate?.pageSize ?? query.pageSize ?? 10,
  };
}

export function getTechManagerDetail<
  T extends TechManagerRecord = TechManagerRecord,
>(resource: TechManagerResource, id: string) {
  return request<T>(`/bff/tech-manager/${resource}/${id}`, { method: 'GET' });
}

export function getTechManagerDemandAnalysisRuns(demandId: string) {
  return request<DemandAnalysisHistory>(
    `/bff/tech-manager/demands/${demandId}/analysis-runs`,
    { method: 'GET' },
  );
}

export function startTechManagerDemandAnalysis(
  demandId: string,
  input: StartDemandAnalysisInput,
) {
  return request<DemandAnalysisResult>(
    `/bff/tech-manager/demands/${demandId}/analysis-runs`,
    { method: 'POST', data: input },
  );
}

export function confirmTechManagerDemandReport(
  demandId: string,
  reportType: DemandReportType,
  input: ConfirmDemandReportInput,
) {
  return request<DemandReportReview>(
    `/bff/tech-manager/demands/${demandId}/reports/${reportType}/confirmations`,
    { method: 'POST', data: input },
  );
}

export function getWorkbench() {
  return request<WorkbenchResponse>('/bff/tech-manager/workbench', {
    method: 'GET',
  });
}

export function getDashboard(query: DashboardQuery = {}) {
  return request<Record<string, unknown>>('/bff/tech-manager/dashboard', {
    method: 'GET',
    params: query,
  });
}

export function getStatistics(topic: StatisticsTopic, query: DashboardQuery = {}) {
  return request<StatisticsResponse>(`/bff/tech-manager/statistics/${topic}`, { method: 'GET', params: query });
}

export function createTechManagerOpportunity<T = TechManagerRecord>(
  demandId: string,
  input: CreateTechManagerOpportunityInput,
) {
  return request<T>(`/bff/tech-manager/demands/${demandId}/opportunities`, {
    method: 'POST',
    data: input,
  });
}

export function createTechManagerResource<T = TechManagerRecord>(
  resource: TechManagerResource,
  input: TechManagerInput,
) {
  return request<T>(`/bff/tech-manager/${resource}`, {
    method: 'POST',
    data: input,
  });
}

export function updateTechManagerResource<T = TechManagerRecord>(
  resource: TechManagerResource,
  id: string,
  input: TechManagerInput,
) {
  return request<T>(`/bff/tech-manager/${resource}/${id}`, {
    method: 'PATCH',
    data: input,
  });
}

export function updateTechManagerResourceStatus<T = TechManagerRecord>(
  resource: TechManagerResource,
  id: string,
  status: string,
) {
  return request<T>(`/bff/tech-manager/${resource}/${id}/status`, {
    method: 'PATCH',
    data: { status },
  });
}

export function updateTechManagerOpportunityStage<T = TechManagerRecord>(
  opportunityId: string,
  stage: string,
) {
  return request<T>(`/bff/tech-manager/opportunities/${opportunityId}/stage`, {
    method: 'PATCH',
    data: { stage },
  });
}

export function confirmTechManagerOpportunity<T = TechManagerRecord>(
  opportunityId: string,
  input: ConfirmTechManagerOpportunityInput,
) {
  return request<T>(
    `/bff/tech-manager/opportunities/${opportunityId}/confirm`,
    {
      method: 'POST',
      data: input,
    },
  );
}

export function revokeTechManagerOpportunityConfirmation<T = TechManagerRecord>(
  opportunityId: string,
  input: {
    expectedVersion: number;
    idempotencyKey: string;
    revocationContent?: string;
  },
) {
  return request<T>(
    `/bff/tech-manager/opportunities/${opportunityId}/confirmation/revoke`,
    { method: 'POST', data: input },
  );
}

export function getTechManagerDeletePreview(
  resource: TechManagerResource,
  id: string,
) {
  return request<TechManagerDeletePreview>(
    `/bff/tech-manager/${resource}/${id}/delete-preview`,
    { method: 'GET' },
  );
}

export function deleteTechManagerResource(
  resource: TechManagerResource,
  id: string,
) {
  return request<TechManagerDeletePreview>(
    `/bff/tech-manager/${resource}/${id}`,
    {
      method: 'DELETE',
    },
  );
}

export function createTechManagerTask<T = TechManagerRecord>(
  entityType: TechManagerEntityType,
  entityId: string,
  input: TechManagerInput,
  idempotencyKey?: string,
) {
  return request<T>(`/bff/tech-manager/${entityType}/${entityId}/tasks`, {
    method: 'POST',
    data: { ...input, ...(idempotencyKey ? { idempotencyKey } : {}) },
  });
}

export function getTechManagerTasks<T = TechManagerRecord>(
  entityType: TechManagerEntityType,
  entityId: string,
) {
  return request<T[]>(`/bff/tech-manager/${entityType}/${entityId}/tasks`, {
    method: 'GET',
  });
}

export function updateTechManagerTask<T = TechManagerRecord>(
  taskId: string,
  input: TechManagerInput,
) {
  return request<T>(`/bff/tech-manager/tasks/${taskId}`, {
    method: 'PATCH',
    data: input,
  });
}

export function completeTechManagerTask<T = TechManagerRecord>(taskId: string) {
  return request<T>(`/bff/tech-manager/tasks/${taskId}/complete`, {
    method: 'PATCH',
  });
}

export function createTechManagerFollowUp<T = TechManagerRecord>(
  entityType: TechManagerEntityType,
  entityId: string,
  input: TechManagerInput,
) {
  return request<T>(`/bff/tech-manager/${entityType}/${entityId}/follow-ups`, {
    method: 'POST',
    data: input,
  });
}

export function getTechManagerFollowUps<T = TechManagerRecord>(
  entityType: TechManagerEntityType,
  entityId: string,
) {
  return request<T[]>(
    `/bff/tech-manager/${entityType}/${entityId}/follow-ups`,
    { method: 'GET' },
  );
}

export function getTechManagerLogs<T = TechManagerRecord>(
  entityType: TechManagerEntityType,
  entityId: string,
) {
  return request<T[]>(`/bff/tech-manager/${entityType}/${entityId}/logs`, {
    method: 'GET',
  });
}

export interface TechManagerActivitySummary {
  thisWeek: number;
  followed: number;
  registered: number;
  pending: number;
  attended: number;
  ended: number;
  newLeads: number;
}

export function getTechManagerActivitySummary() {
  return request<TechManagerActivitySummary>('/bff/tech-manager/activities/summary', {
    method: 'GET',
  });
}

export function followTechManagerActivity<T = TechManagerRecord>(
  activityId: string,
) {
  return request<T>(`/bff/tech-manager/activities/${activityId}/follow`, {
    method: 'PUT',
  });
}

export function unfollowTechManagerActivity<T = TechManagerRecord>(
  activityId: string,
) {
  return request<T>(`/bff/tech-manager/activities/${activityId}/follow`, {
    method: 'DELETE',
  });
}

export function registerTechManagerActivity<T = TechManagerRecord>(
  activityId: string,
) {
  return request<T>(`/bff/tech-manager/activities/${activityId}/registration`, {
    method: 'PUT',
  });
}

export function cancelTechManagerActivityRegistration<T = TechManagerRecord>(
  activityId: string,
) {
  return request<T>(`/bff/tech-manager/activities/${activityId}/registration`, {
    method: 'DELETE',
  });
}

export function attendTechManagerActivity<T = TechManagerRecord>(
  activityId: string,
) {
  return request<T>(`/bff/tech-manager/activities/${activityId}/attendance`, {
    method: 'PUT',
  });
}

export function followTechManagerExpert<T = TechManagerRecord>(
  expertId: string,
) {
  return request<T>(`/bff/tech-manager/experts/${expertId}/follow`, {
    method: 'PUT',
  });
}

export function unfollowTechManagerExpert<T = TechManagerRecord>(
  expertId: string,
) {
  return request<T>(`/bff/tech-manager/experts/${expertId}/follow`, {
    method: 'DELETE',
  });
}

export function createTechManagerLeadFromActivity<T = TechManagerRecord>(
  activityId: string,
  input: CreateTechManagerLeadFromActivityInput,
) {
  return request<T>(`/bff/tech-manager/activities/${activityId}/leads`, {
    method: 'POST',
    data: input,
  });
}

export interface CreateTechManagerLeadFromActivityInput
  extends TechManagerInput {
  name: string;
  idempotencyKey: string;
}

export function convertTechManagerLead<T = ConvertTechManagerLeadResult>(
  leadId: string,
  input: ConvertTechManagerLeadInput,
) {
  return request<T>(`/bff/tech-manager/leads/${leadId}/convert`, {
    method: 'POST',
    data: input,
  });
}

export function copyTechManagerLead<T = LeadRecord>(leadId: string) {
  return request<T>(`/bff/tech-manager/leads/${leadId}/copy`, {
    method: 'POST',
  });
}

export function getTechManagerOrganizationRelations<
  T = Record<string, unknown>,
>(organizationId: string) {
  return request<T>(
    `/bff/tech-manager/organizations/${organizationId}/relations`,
    { method: 'GET' },
  );
}

export function getTechManagerDemandResourceCandidates<
  T = Record<string, unknown>,
>(demandId: string) {
  return request<T>(
    `/bff/tech-manager/demands/${demandId}/resource-candidates`,
    { method: 'GET' },
  );
}

export function addTechManagerDemandResource<T = TechManagerRecord>(
  demandId: string,
  input: TechManagerInput,
) {
  return request<T>(`/bff/tech-manager/demands/${demandId}/resources`, {
    method: 'POST',
    data: input,
  });
}

export function updateTechManagerDemandResource<T = TechManagerRecord>(
  demandId: string,
  relationId: string,
  input: TechManagerInput,
) {
  return request<T>(
    `/bff/tech-manager/demands/${demandId}/resources/${relationId}`,
    { method: 'PATCH', data: input },
  );
}

export interface TechManagerParticipantInput {
  participantType: string;
  participantId: string;
  participantName: string;
  role?: string;
}

export function addTechManagerOpportunityParticipant<T = TechManagerRecord>(
  opportunityId: string,
  input: TechManagerParticipantInput,
) {
  return request<T>(
    `/bff/tech-manager/opportunities/${opportunityId}/participants`,
    { method: 'POST', data: input },
  );
}

export function removeTechManagerOpportunityParticipant<
  T = { removed: boolean },
>(opportunityId: string, participantId: string) {
  return request<T>(
    `/bff/tech-manager/opportunities/${opportunityId}/participants/${participantId}`,
    { method: 'DELETE' },
  );
}
export function listTechManagerDictionaries<T = TechManagerRecord>(
  groupCode?: string,
) {
  return request<T[]>('/bff/tech-manager/business-settings/dictionaries', {
    method: 'GET',
    params: groupCode ? { groupCode } : {},
  });
}

export function createTechManagerDictionary<T = TechManagerRecord>(
  input: TechManagerInput,
) {
  return request<T>('/bff/tech-manager/business-settings/dictionaries', {
    method: 'POST',
    data: input,
  });
}

export function updateTechManagerDictionary<T = TechManagerRecord>(
  dictionaryId: string,
  input: TechManagerInput,
) {
  return request<T>(
    `/bff/tech-manager/business-settings/dictionaries/${dictionaryId}`,
    { method: 'PATCH', data: input },
  );
}

export function deleteTechManagerDictionary<T = TechManagerRecord>(
  dictionaryId: string,
) {
  return request<T>(
    `/bff/tech-manager/business-settings/dictionaries/${dictionaryId}`,
    { method: 'DELETE' },
  );
}

export function getTechManagerAssignees<T = TechManagerRecord>() {
  return request<T[]>('/bff/tech-manager/assignees', { method: 'GET' });
}
